// src/components/admin/GestionMotivosRechazo.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

// Catálogo editable de motivos de rechazo — mismo patrón que GestionReferencias, pero
// solo con nombre (sin color/usuario/fusión, que acá no tienen sentido). El nombre que
// se carga acá es exactamente el valor que va a guardar el turno al finalizar la
// atención (ver TurnoRecepcionEntity.motivoNoAvance) y el que van a mostrar los reportes.
const GestionMotivosRechazo = () => {
    const [motivos, setMotivos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedMotivo, setSelectedMotivo] = useState(null);
    const [nombre, setNombre] = useState('');
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    const [filterNombre, setFilterNombre] = useState('');

    useEffect(() => {
        cargarMotivos();
    }, []);

    const cargarMotivos = async () => {
        setLoading(true);
        try {
            const response = await DatosMaestrosService.getMotivosRechazo();
            setMotivos(response.data);
        } catch {
            mostrarAlerta('Error al cargar los motivos de rechazo', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, motivo = null) => {
        setModalMode(mode);
        if (mode === 'edit' && motivo) {
            setSelectedMotivo(motivo);
            setNombre(motivo.nombre);
        } else {
            setSelectedMotivo(null);
            setNombre('');
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedMotivo(null);
        setNombre('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createMotivoRechazo({ nombre });
                mostrarAlerta('Motivo de rechazo creado exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateMotivoRechazo(selectedMotivo.id, { nombre });
                mostrarAlerta('Motivo de rechazo actualizado exitosamente', 'success');
            }
            handleCloseModal();
            cargarMotivos();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || 'Error al guardar el motivo de rechazo', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este motivo de rechazo? No afecta a los turnos que ya lo tienen cargado.')) return;
        setLoading(true);
        try {
            await DatosMaestrosService.deleteMotivoRechazo(id);
            mostrarAlerta('Motivo de rechazo eliminado exitosamente', 'success');
            cargarMotivos();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || 'Error al eliminar el motivo de rechazo', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const motivosFiltrados = motivos.filter(m =>
        coincideBusqueda(m.nombre, filterNombre)
    );

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end flex-wrap gap-2 mb-3">
                <Button variant="primary" onClick={() => handleOpenModal('create')}>
                    <i className="bi bi-plus-circle me-2"></i>
                    Nuevo Motivo
                </Button>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${motivosFiltrados.length} de ${motivos.length} motivos`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-motivo-nombre" ancho>
                    <Form.Control
                        id="filtro-motivo-nombre"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre del motivo…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && motivos.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                        <tr>
                            <th>Nombre</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {motivosFiltrados.length === 0 ? (
                            <tr>
                                <td colSpan="2" className="text-center text-muted py-4">
                                    {motivos.length === 0
                                        ? "No hay motivos de rechazo registrados"
                                        : "No se encontraron motivos que coincidan con la búsqueda"}
                                </td>
                            </tr>
                        ) : (
                            motivosFiltrados.map((motivo) => (
                                <tr key={motivo.id}>
                                    <td className="fw-semibold">{motivo.nombre}</td>
                                    <td className="text-end">
                                        <MenuAccionesFila acciones={[
                                            { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', motivo) },
                                            { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(motivo.id) },
                                        ]} />
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nuevo Motivo de Rechazo' : 'Editar Motivo de Rechazo'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                required
                                placeholder="Ej: No cumple requisito diferencial"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default GestionMotivosRechazo;
